import type { SourceSpan } from "@magic-md/core";
import type { PreviewAnnotation } from "./types";

type HastNode = {
    type?: string;
    tagName?: string;
    value?: string;
    properties?: Record<string, unknown>;
    children?: HastNode[];
};

interface AnnotationSourceRehypeOptions {
    documentPath: string;
    content: string;
    source: SourceSpan;
    annotations: PreviewAnnotation[];
}

interface SourceInterval {
    id: string;
    kind: PreviewAnnotation["style"]["kind"];
    start: number;
    end: number;
}

export function createAnnotationSourceRehypePlugin(
    options: AnnotationSourceRehypeOptions,
) {
    return function annotationSourceRehypePlugin() {
        return (tree: HastNode) => {
            const intervals = getLocalIntervals(options);
            let cursor = 0;
            transformChildren(tree, intervals, options, () => cursor, (next) => {
                cursor = next;
            });
        };
    };
}

function transformChildren(
    node: HastNode,
    intervals: SourceInterval[],
    options: AnnotationSourceRehypeOptions,
    getCursor: () => number,
    setCursor: (next: number) => void,
) {
    if (!node.children || shouldSkipNode(node)) return;
    for (let index = 0; index < node.children.length; index++) {
        const child = node.children[index];
        if (!child) continue;
        if (child.type === "text") {
            const replacement = wrapTextNode(child, intervals, options, getCursor, setCursor);
            if (replacement) {
                node.children.splice(index, 1, ...replacement);
                index += replacement.length - 1;
            }
            continue;
        }
        transformChildren(child, intervals, options, getCursor, setCursor);
    }
}

function wrapTextNode(
    node: HastNode,
    intervals: SourceInterval[],
    options: AnnotationSourceRehypeOptions,
    getCursor: () => number,
    setCursor: (next: number) => void,
): HastNode[] | null {
    const value = node.value ?? "";
    if (!value) return null;
    const localStart = findVisibleTextOffset(options.content, value, getCursor());
    if (localStart < 0) return null;
    const localEnd = localStart + value.length;
    setCursor(localEnd);

    const activeIntervals = intervals.filter(
        (interval) => interval.start < localEnd && interval.end > localStart,
    );
    if (activeIntervals.length === 0) {
        return [createSourceSpanNode(value, options, localStart, localEnd, [])];
    }

    const breakpoints = new Set<number>([0, value.length]);
    for (const interval of activeIntervals) {
        breakpoints.add(Math.max(0, interval.start - localStart));
        breakpoints.add(Math.min(value.length, interval.end - localStart));
    }
    const points = Array.from(breakpoints).sort((a, b) => a - b);
    const nodes: HastNode[] = [];
    for (let i = 0; i < points.length - 1; i++) {
        const from = points[i] ?? 0;
        const to = points[i + 1] ?? from;
        if (to <= from) continue;
        const segmentStart = localStart + from;
        const segmentEnd = localStart + to;
        const segmentIntervals = activeIntervals.filter(
            (interval) => interval.start < segmentEnd && interval.end > segmentStart,
        );
        nodes.push(
            createSourceSpanNode(
                value.slice(from, to),
                options,
                segmentStart,
                segmentEnd,
                segmentIntervals,
            ),
        );
    }
    return nodes;
}

function createSourceSpanNode(
    text: string,
    options: AnnotationSourceRehypeOptions,
    localStart: number,
    localEnd: number,
    intervals: SourceInterval[],
): HastNode {
    const classes = ["magic-md-annotation-source"];
    if (intervals.length > 0) {
        classes.push("magic-md-annotation-mark");
        if (intervals.some((interval) => interval.kind === "highlight")) {
            classes.push("magic-md-annotation-highlight");
        }
        if (intervals.some((interval) => interval.kind === "underline")) {
            classes.push("magic-md-annotation-underline");
        }
        if (intervals.some((interval) => interval.kind === "comment")) {
            classes.push("magic-md-annotation-comment");
        }
    }
    return {
        type: "element",
        tagName: "span",
        properties: {
            className: classes,
            "data-md-source-start": String(options.source.startOffset + localStart),
            "data-md-source-end": String(options.source.startOffset + localEnd),
            "data-md-annotation-ids": intervals.map((interval) => interval.id).join(" "),
        },
        children: [{ type: "text", value: text }],
    };
}

function getLocalIntervals(options: AnnotationSourceRehypeOptions): SourceInterval[] {
    return options.annotations
        .filter((annotation) => {
            if (annotation.status !== "open") return false;
            const target = annotation.target;
            return (
                target.kind === "source-text" &&
                target.documentPath === options.documentPath &&
                target.endOffset > options.source.startOffset &&
                target.startOffset < options.source.endOffset
            );
        })
        .map((annotation) => {
            const target = annotation.target;
            if (target.kind !== "source-text") {
                throw new Error("unreachable annotation target");
            }
            const visualRange = trimVisualRange(
                target.startOffset,
                target.endOffset,
                target.quote,
            );
            return {
                id: annotation.id,
                kind: annotation.style.kind,
                start: Math.max(0, visualRange.start - options.source.startOffset),
                end: Math.min(
                    options.content.length,
                    visualRange.end - options.source.startOffset,
                ),
            };
        })
        .filter((interval) => interval.end > interval.start);
}

function trimVisualRange(
    startOffset: number,
    endOffset: number,
    quote: string,
): { start: number; end: number } {
    const leading = quote.match(/^\s+/)?.[0].length ?? 0;
    const trailing = quote.match(/\s+$/)?.[0].length ?? 0;
    return {
        start: Math.min(endOffset, startOffset + leading),
        end: Math.max(startOffset, endOffset - trailing),
    };
}

function findVisibleTextOffset(
    content: string,
    text: string,
    cursor: number,
): number {
    const exact = content.indexOf(text, cursor);
    if (exact >= 0) return exact;
    const trimmed = text.trim();
    if (!trimmed) return cursor;
    const fuzzy = content.indexOf(trimmed, cursor);
    return fuzzy >= 0 ? fuzzy : -1;
}

function shouldSkipNode(node: HastNode): boolean {
    return node.tagName === "pre" || node.tagName === "code";
}
